import React, { useState, useEffect } from "react";
import Layout from "../layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaTimes, FaChevronLeft, FaChevronRight, FaImages } from 'react-icons/fa';

const images = [
  "assets/images/G/g123.jpeg",
  "assets/images/G/g124.jpeg",
  "assets/images/G/g125.jpeg",
  "assets/images/G/g126.jpeg",
  "assets/images/G/g7.jpeg",
  "assets/images/G/g8.jpeg",
  "assets/images/G/g10.jpeg",
  "assets/images/G/g12.jpeg",
  "assets/images/G/g13.jpeg",
  "assets/images/G/g15.jpeg",
  "assets/images/G/g6.jpeg",
];

const categories = [
  { id: 'all', name: 'All Images', icon: <FaImages /> },
  { id: 'dashboard', name: 'Dashboard', icon: <FaImages /> },
  { id: 'features', name: 'Features', icon: <FaImages /> },
  { id: 'mobile', name: 'Mobile App', icon: <FaImages /> },
];

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const [filteredImages, setFilteredImages] = useState(images);

  useEffect(() => {
    AOS.init({ 
      duration: 1000, 
      once: true,
      offset: 100
    });
  }, []);

  useEffect(() => {
    // For now, all images are shown. You can add category filtering logic here
    setFilteredImages(images);
  }, [activeCategory]);

  const openModal = (img, index) => {
    setSelectedImage(img);
    setCurrentIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(filteredImages[prevIndex]);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (selectedImage) {
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
        if (e.key === 'Escape') closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage, currentIndex]);

  return (
    <Layout>
      <Helmet>
        <title>Gallery - Educational Eternity | Platform Screenshots & Features</title>
        <meta name="description" content="Explore our gallery showcasing Educational Eternity platform screenshots, features, and interface designs." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative w-full h-[60vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url(assets/images/123.gif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        <div className="relative z-10 text-center px-4">
          <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
            Our Gallery
          </h1>
          <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
            Explore our platform through stunning screenshots and feature showcases
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2 ${
                  activeCategory === category.id
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaImages className="text-sm" />
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid - Masonry Style */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((src, index) => (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 50}
                onClick={() => openModal(src, index)}
                className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer transform hover:scale-105 transition-all duration-500 bg-white"
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={src}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-125"
                    loading="lazy"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                    <div className="text-center">
                      <div className="text-white text-lg font-bold mb-1">View Full Image</div>
                      <div className="text-white/80 text-sm">Click to expand</div>
                    </div>
                  </div>
                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </div>
                {/* Image Number Badge */}
                <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-md text-white px-3 py-1 rounded-full text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  {index + 1} / {filteredImages.length}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={closeModal}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white hover:text-gray-300 text-4xl font-bold z-50 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-all"
            aria-label="Close"
          >
            <FaTimes />
          </button>

          {/* Navigation Buttons */}
          {filteredImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-all z-50"
                aria-label="Previous"
              >
                <FaChevronLeft className="text-xl" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-black/70 transition-all z-50"
                aria-label="Next"
              >
                <FaChevronRight className="text-xl" />
              </button>
            </>
          )}

          {/* Image Container */}
          <div 
            className="relative max-w-7xl w-full max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedImage} 
              alt="Gallery" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" 
            />
            
            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-md text-white px-6 py-3 rounded-full text-sm font-semibold">
              {currentIndex + 1} / {filteredImages.length}
            </div>
          </div>

          {/* Keyboard Hint */}
          <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 text-white/60 text-xs hidden md:block">
            Use ← → arrow keys to navigate
          </div>
        </div>
      )}

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: images.length, label: "Screenshots" },
              { number: "100%", label: "Responsive Design" },
              { number: "24/7", label: "Platform Access" },
              { number: "50K+", label: "Active Users" }
            ].map((stat, idx) => (
              <div key={idx} data-aos="fade-up" data-aos-delay={idx * 100} className="text-white">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-blue-200 font-semibold">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default Gallery;
